import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { BottlingStatusBadge } from "@/components/bottling/BottlingStatusBadge";
import { ConfirmDateModal, UnlockModal, MarkCompleteModal, ExportModal } from "@/components/bottling/BottlingModals";
import {
  useBottlingAdminEvents,
  useBottlingEvent,
  useBottlingSkus,
  useBottlingRevisions,
  useUpdateBottlingEventStatus,
  useLogBottlingRevision,
  useExportBottlingEvents,
} from "@/hooks/useBottlingEvents";
import { useAuth } from "@/contexts/AuthContext";
import { fmtDate, fmtDateShort, PAGE_SIZE, type BottlingEventStatus } from "@/lib/bottling-constants";

// ─── Search helper ────────────────────────────────────────────────────────

function searchEvents(events: any[], query: string) {
  if (!query) return events;
  const q = query.toLowerCase();
  return events.filter(e => {
    const client = e.client?.display_name || "";
    return (
      e.name?.toLowerCase().includes(q) ||
      client.toLowerCase().includes(q) ||
      e.status?.toLowerCase().includes(q) ||
      e.confirmed_date?.includes(q)
    );
  });
}

// ─── CSV export helper ────────────────────────────────────────────────────

function exportCsv(events: any[], inclEvents: boolean, inclSkus: boolean) {
  const files: { name: string; content: string }[] = [];
  if (inclEvents) {
    const headers = ["Bottling Date", "Client", "Status", "Facility", "Preferred From", "Preferred To", "Confirmed Date", "Lock Date", "Est. Cases"];
    const rows = events.map(e => [e.name, e.client?.display_name || "", e.status, e.facility || "", e.preferred_date_from || "", e.preferred_date_to || "", e.confirmed_date || "", e.lock_date || "", e.est_cases || ""]);
    files.push({ name: "events.csv", content: [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n") });
  }
  if (inclSkus) {
    const headers = ["Bottling Date", "Client", "Brand", "Wine", "Vintage", "Bottle", "Format", "Closure", "Case Pack", "Est. Cases", "Filter", "DMDC", "Lot Code"];
    const rows = events.flatMap(e => (e.bottling_sku || []).map((s: any) => [e.name, e.client?.display_name || "", s.brand, s.wine, s.vintage || "", s.bottle_type || "", s.bottle_format || "", s.closure || "", s.case_pack, s.est_cases || "", s.filter_type || "", s.dmdc ? "Yes" : "No", s.lot_code || ""]));
    files.push({ name: "skus.csv", content: [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n") });
  }
  files.forEach(f => {
    const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(new Blob([f.content], { type: "text/csv" })), download: `gravity-bottling-${f.name}` });
    a.click();
  });
}

// ─── Main Bottling page ───────────────────────────────────────────────────

export default function Bottling() {
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get("event");

  if (eventId) {
    return <AdminEventReview eventId={eventId} />;
  }
  return <AdminDashboard />;
}

// ─── Admin Dashboard ──────────────────────────────────────────────────────

function AdminDashboard() {
  const navigate = useNavigate();
  const { data: events = [], isLoading } = useBottlingAdminEvents();
  const exportMutation = useExportBottlingEvents();
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [showExport, setShowExport] = useState(false);

  const statusFiltered = filter === "all" ? events : events.filter((e: any) => e.status === filter);
  const searched = searchEvents(statusFiltered, query);
  const [page, setPage] = useState(1);
  const totalPages = Math.ceil(searched.length / PAGE_SIZE);
  const pageItems = searched.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const counts = events.reduce((acc: Record<string, number>, e: any) => {
    acc[e.status] = (acc[e.status] || 0) + 1;
    return acc;
  }, {});

  async function handleExport({ status, format, dateFrom, dateTo, inclEvents, inclSkus }: any) {
    try {
      const data = await exportMutation.mutateAsync({ status, dateFrom, dateTo });
      if (!data?.length) { alert("No events match your filters."); return; }
      exportCsv(data, inclEvents, inclSkus);
      setShowExport(false);
    } catch {
      alert("Export failed.");
    }
  }

  return (
    <div className="mx-auto max-w-6xl px-8 py-10">
      <div className="mb-8 flex items-end justify-between border-b pb-5">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Bottling Queue</h1>
          <p className="mt-1.5 text-sm text-muted-foreground">Review and manage client bottling requests</p>
        </div>
        <Button variant="ghost" onClick={() => setShowExport(true)}>
          <Download className="h-4 w-4 mr-2" /> Export
        </Button>
      </div>

      {/* Toolbar */}
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <Input
          placeholder="Search client, wine, status…"
          onChange={e => { setQuery(e.target.value); setPage(1); }}
          className="w-72"
        />
        <div className="flex gap-1.5">
          {(["all", "submitted", "confirmed", "locked", "completed"] as const).map(s => (
            <button
              key={s}
              onClick={() => { setFilter(s); setPage(1); }}
              className={`flex items-center gap-1.5 rounded px-3 py-1.5 text-[11px] font-medium uppercase tracking-wider border transition-colors ${
                filter === s
                  ? "bg-primary/10 text-primary border-primary/30"
                  : "text-muted-foreground border-border hover:text-foreground hover:border-muted-foreground bg-transparent"
              }`}
            >
              {s === "all" ? "All" : s.charAt(0).toUpperCase() + s.slice(1)}
              {s !== "all" && counts[s] ? (
                <span className="flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[9px] text-primary-foreground">
                  {counts[s]}
                </span>
              ) : null}
            </button>
          ))}
        </div>
      </div>

      {query && (
        <p className="mb-3 text-[11px] text-muted-foreground">
          {searched.length} result{searched.length !== 1 ? "s" : ""} for &quot;{query}&quot;
        </p>
      )}

      {/* Table */}
      <Card>
        {isLoading ? (
          <div className="px-6 py-10 text-center text-sm text-muted-foreground">Loading…</div>
        ) : pageItems.length === 0 ? (
          <div className="px-6 py-10 text-center text-sm text-muted-foreground">
            {query ? `No events match "${query}"` : "No events found."}
          </div>
        ) : (
          <>
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr>
                  {["Client", "Preferred Window", "Confirmed Date", "SKUs", "Cases", "Status", ""].map((h, i) => (
                    <th key={i} className="border-b bg-muted px-4 py-2.5 text-left text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pageItems.map((e: any) => {
                  const client = e.client?.display_name || "—";
                  return (
                    <tr
                      key={e.id}
                      className="cursor-pointer border-b last:border-0 hover:bg-muted/50 transition-colors"
                      onClick={() => navigate(`/apps/bottling?event=${e.id}`)}
                    >
                      <td className="px-4 py-3.5">
                        <div className="font-medium">{client}</div>
                        <div className="mt-0.5 text-[11px] text-muted-foreground">{e.name}</div>
                      </td>
                      <td className="px-4 py-3.5 text-muted-foreground">{e.preferred_date_from ? fmtDateShort(e.preferred_date_from) : "—"}</td>
                      <td className="px-4 py-3.5">{e.confirmed_date ? fmtDateShort(e.confirmed_date) : <span className="text-muted-foreground">Pending</span>}</td>
                      <td className="px-4 py-3.5 font-mono text-sm">—</td>
                      <td className="px-4 py-3.5 font-mono text-sm">{e.est_cases?.toLocaleString() || "—"}</td>
                      <td className="px-4 py-3.5"><BottlingStatusBadge status={e.status} /></td>
                      <td className="px-4 py-3.5 text-right"><Button variant="ghost" size="sm">Review →</Button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div className="flex items-center justify-between px-5 py-3 border-t text-sm text-muted-foreground">
                <span>{searched.length} total</span>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
                  <span className="px-2 py-1">Page {page} of {totalPages}</span>
                  <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      <ExportModal open={showExport} onClose={() => setShowExport(false)} onExport={handleExport} />
    </div>
  );
}

// ─── Admin Event Review ───────────────────────────────────────────────────

function AdminEventReview({ eventId }: { eventId: string }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: evt, isLoading: loadingEvt } = useBottlingEvent(eventId);
  const { data: skus = [] } = useBottlingSkus(eventId);
  const { data: revisions = [] } = useBottlingRevisions(eventId);
  const updateStatus = useUpdateBottlingEventStatus();
  const logRevision = useLogBottlingRevision();

  const [showConfirm, setShowConfirm] = useState(false);
  const [showUnlock, setShowUnlock] = useState(false);
  const [showComplete, setShowComplete] = useState(false);

  async function handleConfirmDate(date: string, lockDate: string | null) {
    if (!date) { alert("Please select a date."); return; }
    await updateStatus.mutateAsync({ eventId, status: "confirmed", extraFields: { confirmed_date: date, lock_date: lockDate } });
    await logRevision.mutateAsync({ eventId, changedBy: user!.id, changeType: "status_change", summary: `Date confirmed: ${date}` });
    setShowConfirm(false);
  }

  async function handleLock() {
    await updateStatus.mutateAsync({ eventId, status: "locked" });
    await logRevision.mutateAsync({ eventId, changedBy: user!.id, changeType: "status_change", summary: "Event locked by admin." });
  }

  async function handleUnlock(note: string) {
    await updateStatus.mutateAsync({ eventId, status: "draft" });
    await logRevision.mutateAsync({ eventId, changedBy: user!.id, changeType: "status_change", summary: `Unlocked: ${note}` });
    setShowUnlock(false);
  }

  async function handleComplete() {
    await updateStatus.mutateAsync({ eventId, status: "completed" });
    await logRevision.mutateAsync({ eventId, changedBy: user!.id, changeType: "status_change", summary: "Event marked complete." });
    setShowComplete(false);
  }

  if (loadingEvt) return <div className="p-10 text-center text-muted-foreground">Loading…</div>;
  if (!evt) return <div className="p-10 text-center text-muted-foreground">Event not found.</div>;

  const totalCases = skus.reduce((s: number, x: any) => s + Number(x.est_cases || 0), 0);
  const status = evt.status as BottlingEventStatus;

  return (
    <div className="mx-auto max-w-5xl px-8 py-10">
      <button
        className="mb-6 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
        onClick={() => navigate("/apps/bottling")}
      >
        ← Back to Queue
      </button>

      {/* Header */}
      <div className="mb-8 flex items-end justify-between border-b pb-5">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-semibold tracking-tight">{evt.name || "Bottling Event"}</h1>
            <BottlingStatusBadge status={status} />
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            {evt.confirmed_date
              ? <>Confirmed: <strong className="text-foreground">{fmtDate(evt.confirmed_date)}</strong> · Lock: {evt.lock_date ? fmtDate(evt.lock_date) : "—"}</>
              : "No confirmed date yet"}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {status === "submitted" && <Button onClick={() => setShowConfirm(true)}>Confirm Date</Button>}
          {status === "confirmed" && (
            <>
              <Button variant="ghost" onClick={() => setShowUnlock(true)}>Request Changes</Button>
              <Button onClick={handleLock}>Lock Event</Button>
            </>
          )}
          {status === "locked" && (
            <>
              <Button variant="ghost" onClick={() => setShowUnlock(true)}>Unlock</Button>
              <Button onClick={() => setShowComplete(true)}>Mark Complete</Button>
            </>
          )}
          {(["draft", "submitted"] as string[]).includes(status) && (
            <Button variant="ghost" onClick={() => setShowConfirm(true)}>Assign Date</Button>
          )}
        </div>
      </div>

      {/* Info + History grid */}
      <div className="mb-6 grid grid-cols-2 gap-5">
        <Card>
          <CardHeader><CardTitle>Event Info</CardTitle></CardHeader>
          <CardContent className="divide-y text-sm">
            {([
              ["Facility", evt.facility || "Gravity Wine House"],
              ["Preferred From", fmtDate(evt.preferred_date_from)],
              ["Preferred To", fmtDate(evt.preferred_date_to)],
              ["Confirmed Date", fmtDate(evt.confirmed_date)],
              ["Lock Date", fmtDate(evt.lock_date)],
              ["Est. Cases", evt.est_cases?.toLocaleString() || "—"],
              ...(evt.client_notes ? [["Client Notes", evt.client_notes]] : []),
            ] as [string, string][]).map(([label, value]) => (
              <div key={label} className="flex justify-between py-2">
                <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
                <span>{value}</span>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>History</CardTitle></CardHeader>
          <div className="max-h-60 overflow-y-auto divide-y px-5">
            {revisions.length === 0 ? (
              <p className="py-4 text-sm text-muted-foreground">No revision history yet.</p>
            ) : (
              revisions.map((r: any, i: number) => (
                <div key={i} className="py-3">
                  <div className="text-[11px] text-muted-foreground mb-1">
                    {new Date(r.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                    {" · "}{r.changed_by_user?.display_name || "System"}
                    {" · "}<span className="uppercase">{r.change_type}</span>
                  </div>
                  <div className="text-sm">{r.summary}</div>
                </div>
              ))
            )}
          </div>
        </Card>
      </div>

      {/* SKU table */}
      <Card>
        <CardHeader>
          <CardTitle>Wines / SKUs</CardTitle>
          <span className="text-[12px] text-muted-foreground">
            {skus.length} SKU{skus.length !== 1 ? "s" : ""} · {totalCases.toLocaleString()} cases
          </span>
        </CardHeader>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] border-collapse text-sm">
            <thead>
              <tr>
                {["#", "Brand", "Wine", "Vintage", "Bottle", "Format", "Closure", "Label", "Capsule", "Pack", "Cases", "Filter", "DMDC", "Lot Code", "Instructions"].map((h, i) => (
                  <th key={i} className="border-b bg-muted px-3 py-2.5 text-left text-[10px] font-medium uppercase tracking-wider text-muted-foreground whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {skus.map((s: any, i: number) => (
                <tr key={s.id || i} className="border-b last:border-0">
                  <td className="px-3 py-3 text-[11px] text-muted-foreground">{i + 1}</td>
                  <td className="px-3 py-3">{s.brand}</td>
                  <td className="px-3 py-3 font-medium">{s.wine}</td>
                  <td className="px-3 py-3">{s.vintage}</td>
                  <td className="px-3 py-3">{s.bottle_type}</td>
                  <td className="px-3 py-3">{s.bottle_format || "750ml"}</td>
                  <td className="px-3 py-3">{s.closure}</td>
                  <td className="px-3 py-3">{s.label || "Yes"}</td>
                  <td className="px-3 py-3">{s.capsule ? "Yes" : "No"}</td>
                  <td className="px-3 py-3 text-right font-mono">{s.case_pack}</td>
                  <td className="px-3 py-3 text-right font-mono font-semibold">{s.est_cases}</td>
                  <td className="px-3 py-3">{s.filter_type || "—"}</td>
                  <td className="px-3 py-3">{s.dmdc ? "Yes" : "No"}</td>
                  <td className="px-3 py-3 font-mono text-xs">{s.lot_code || "—"}</td>
                  <td className="px-3 py-3 text-xs text-muted-foreground">{s.instructions || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <ConfirmDateModal open={showConfirm} onClose={() => setShowConfirm(false)} onConfirm={handleConfirmDate} />
      <UnlockModal open={showUnlock} onClose={() => setShowUnlock(false)} onConfirm={handleUnlock} />
      <MarkCompleteModal open={showComplete} onClose={() => setShowComplete(false)} onConfirm={handleComplete} />
    </div>
  );
}
